import React, { useContext, useMemo } from "react";
import { Box, Chip, Skeleton, Typography, useMediaQuery, useTheme } from "@mui/material";
import Grid from "@mui/material/Grid";
import { DiveInPhase, Experience, Skill } from "src/experiences/experienceService/experiences.types";
import { Theme } from "@mui/material/styles";
import HelpTip from "src/theme/HelpTip/HelpTip";
import InfoIcon from "@mui/icons-material/Info";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import PrimaryIconButton from "src/theme/PrimaryIconButton/PrimaryIconButton";
import ContextMenu from "src/theme/ContextMenu/ContextMenu";
import { MenuItemConfig } from "src/theme/ContextMenu/menuItemConfig.types";
import { IsOnlineContext } from "src/app/isOnlineProvider/IsOnlineProvider";
import RestoreIcon from "@mui/icons-material/Restore";
import SkillPopover from "src/experiences/experiencesDrawer/components/skillPopover/SkillPopover";
import { deduplicateSkills } from "src/utils/skillsUtils";
import { useTranslation } from "react-i18next";
import { getProductName } from "src/envService";

const uniqueId = "34a59a9e-e7f6-4a10-8b72-0fd401c727de";

export const DATA_TEST_ID = {
  LOADING_EXPERIENCES_DRAWER_CONTENT_CONTAINER: `experiences-drawer-content-container-${uniqueId}`,
  EXPERIENCES_DRAWER_CONTENT_CONTAINER: `experiences-drawer-content-container-${uniqueId}`,
  EXPERIENCES_DRAWER_CONTENT_DATE: `experiences-drawer-content-date-${uniqueId}`,
  EXPERIENCES_DRAWER_CONTENT_OCCUPATION: `experiences-drawer-content-occupation-${uniqueId}`,
  EXPERIENCES_DRAWER_CONTENT_SKILLS: `experiences-drawer-content-skills-${uniqueId}`,
  EXPERIENCES_DRAWER_SKILLS_CONTAINER: `experiences-drawer-skills-container-${uniqueId}`,
  EXPERIENCES_DRAWER_CONTENT_SUMMARY: `experiences-drawer-content-summary-${uniqueId}`,
  EXPERIENCES_DRAWER_CHIP: `experiences-drawer-chip-${uniqueId}`,
  EXPERIENCES_DRAWER_MORE_BUTTON: `experiences-drawer-more-button-${uniqueId}`,
  EXPERIENCES_DRAWER_RESTORE_TO_ORIGINAL_BUTTON: `experiences-drawer-restore-to-original-button-${uniqueId}`,
  RESTORE_EXPERIENCE_BUTTON: `restore-experience-button-${uniqueId}`,
};

export const MENU_ITEM_ID = {
  EDIT: `experiences-drawer-menu-item-edit-${uniqueId}`,
  DELETE: `experiences-drawer-menu-item-delete-${uniqueId}`,
  RESTORE_TO_ORIGINAL: `experiences-drawer-menu-item-restore-to-original-${uniqueId}`,
};

export const MENU_ITEM_TEXT = {
  EDIT: "experiences.experiencesDrawer.components.experiencesDrawerContent.menu.edit",
  DELETE: "experiences.experiencesDrawer.components.experiencesDrawerContent.menu.delete",
  REVERT: "experiences.experiencesDrawer.components.experiencesDrawerContent.menu.revert",
} as const;

interface ExperienceProps {
  experience: Experience;
  onEdit?: (experience: Experience) => void;
  onDelete?: (experience: Experience) => void;
  onRestoreToOriginal?: (experience: Experience) => void;
}

export const capitalizeFirstLetter = (string: string): string => {
  return string.charAt(0).toUpperCase() + string.slice(1);
};

const ExperiencesDrawerContent: React.FC<ExperienceProps> = ({ experience, onEdit, onDelete, onRestoreToOriginal }) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const appName = getProductName();
  const isOnline = useContext(IsOnlineContext);
  const isSmallMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down("sm"));
  const [selectedSkill, setSelectedSkill] = React.useState<Skill | null>(null);
  const [popoverAnchorEl, setPopoverAnchorEl] = React.useState<HTMLElement | null>(null);
  const [moreMenuAnchorEl, setMoreMenuAnchorEl] = React.useState<HTMLElement | null>(null);

  const formattedSkills = useMemo(() => {
    if (experience.top_skills.length === 0) return [];
    // Deduplicate skills to handle any duplicates that might exist
    return deduplicateSkills(experience.top_skills).uniqueSkills;
  }, [experience.top_skills]);

  const handleEditClick = () => {
    onEdit && onEdit(experience);
    setMoreMenuAnchorEl(null);
  };

  const handleMoreClick = (event: React.MouseEvent<HTMLElement>) => {
    setMoreMenuAnchorEl(event.currentTarget);
  };

  const handleDeleteClick = () => {
    onDelete && onDelete && onDelete(experience);
    setMoreMenuAnchorEl(null);
  };

  const handleRestoreToOriginalClick = () => {
    onRestoreToOriginal && onRestoreToOriginal(experience);
    setMoreMenuAnchorEl(null);
  };

  const isExplored = experience.exploration_phase === DiveInPhase.PROCESSED;

  const getContextMenuHeaderMessage = () => {
    if (!isExplored) {
      return t("experiences.experiencesDrawer.components.experiencesDrawerContent.actionsUnavailableUntilExplored", {
        appName,
      });
    }
    return undefined;
  };

  const getMoreMenuItems = (): MenuItemConfig[] => {
    return [
      {
        id: MENU_ITEM_ID.EDIT,
        text: t(MENU_ITEM_TEXT.EDIT),
        icon: <EditIcon />,
        disabled: !isExplored,
        action: handleEditClick,
      },
      {
        id: MENU_ITEM_ID.RESTORE_TO_ORIGINAL,
        text: t(MENU_ITEM_TEXT.REVERT),
        icon: <RestoreIcon />,
        disabled: !isOnline || !isExplored,
        action: handleRestoreToOriginalClick,
      },
      {
        id: MENU_ITEM_ID.DELETE,
        text: t(MENU_ITEM_TEXT.DELETE),
        icon: <DeleteIcon sx={{ color: theme.palette.error.main }} />,
        disabled: !isOnline || !isExplored,
        action: handleDeleteClick,
        textColor: theme.palette.error.main,
      },
    ];
  };

  const handleChipClick = (event: React.MouseEvent<HTMLElement>, skill: Skill) => {
    setPopoverAnchorEl(event.currentTarget);
    setSelectedSkill(skill);
  };

  const displayTitle = experience.normalized_experience_title ?? experience.experience_title;

  return (
    <Box
      display="flex"
      flexDirection="column"
      gap={isSmallMobile ? theme.fixedSpacing(theme.tabiyaSpacing.sm) : theme.fixedSpacing(theme.tabiyaSpacing.md)}
      data-testid={DATA_TEST_ID.EXPERIENCES_DRAWER_CONTENT_CONTAINER}
    >
      <Box display="flex" flexDirection="column" gap={theme.fixedSpacing(theme.tabiyaSpacing.xs)}>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Typography
            variant="body2"
            fontWeight="bold"
            data-testid={DATA_TEST_ID.EXPERIENCES_DRAWER_CONTENT_OCCUPATION}
          >
            {displayTitle ? (
              <>
                {displayTitle}
                {experience.company && ` — ${experience.company}`}
              </>
            ) : (
              <i>{t("experiences.experiencesDrawer.components.experiencesDrawerContent.untitled")}</i>
            )}
          </Typography>
          <Box display="flex" alignItems="center" justifyContent="flex-end">
            <PrimaryIconButton
              onClick={handleMoreClick}
              sx={{ color: theme.palette.common.black }}
              title={t("experiences.experiencesDrawer.components.experiencesDrawerContent.moreOptionsTitle")}
              data-testid={DATA_TEST_ID.EXPERIENCES_DRAWER_MORE_BUTTON}
            >
              <MoreVertIcon />
            </PrimaryIconButton>
          </Box>
        </Box>
        <Typography variant="caption" fontWeight={500} data-testid={DATA_TEST_ID.EXPERIENCES_DRAWER_CONTENT_DATE}>
          {/* display the start and end dates */}
          {experience.timeline.end && experience.timeline.start
            ? `${experience.timeline.start} — ${experience.timeline.end}`
            : experience.timeline.start || experience.timeline.end}

          {/* display location separated by pipe */}
          {(experience.timeline.start || experience.timeline.end) && experience.location && " | "}
          {experience.location && <i>{experience.location}</i>}
        </Typography>
      </Box>
      {experience.summary && (
        <Typography variant="body2" data-testid={DATA_TEST_ID.EXPERIENCES_DRAWER_CONTENT_SUMMARY}>
          {experience.summary}
        </Typography>
      )}
      <Box display="flex" alignItems="center">
        <Typography
          variant="body1"
          sx={{ wordBreak: "break-all" }}
          data-testid={DATA_TEST_ID.EXPERIENCES_DRAWER_CONTENT_SKILLS}
        >
          <b>{t("experiences.experiencesDrawer.components.experiencesDrawerContent.topSkillsLabel")}</b>
        </Typography>
        <HelpTip icon={<InfoIcon sx={{ padding: 0.1 }} />}>
          {t("experiences.experiencesDrawer.components.experiencesDrawerContent.topSkillsHelp")}
        </HelpTip>
      </Box>
      <Box
        display="flex"
        flexDirection="row"
        alignItems="center"
        flexWrap="wrap"
        gap={2}
        data-testid={DATA_TEST_ID.EXPERIENCES_DRAWER_SKILLS_CONTAINER}
      >
        {formattedSkills.length === 0 ? (
          <Typography>{t("experiences.experiencesDrawer.components.experiencesDrawerContent.noSkillsYet")}</Typography>
        ) : (
          formattedSkills.map((skill) => (
            <Chip
              key={skill.UUID}
              label={capitalizeFirstLetter(skill.preferredLabel)}
              sx={{ color: theme.palette.text.primary, backgroundColor: theme.palette.grey[100] }}
              onClick={(event) => handleChipClick(event, skill)}
              data-testid={DATA_TEST_ID.EXPERIENCES_DRAWER_CHIP}
            />
          ))
        )}
      </Box>
      <SkillPopover
        open={Boolean(popoverAnchorEl)}
        anchorEl={popoverAnchorEl}
        onClose={() => setPopoverAnchorEl(null)}
        skill={selectedSkill}
      />
      <ContextMenu
        anchorEl={moreMenuAnchorEl}
        open={Boolean(moreMenuAnchorEl)}
        notifyOnClose={() => setMoreMenuAnchorEl(null)}
        items={getMoreMenuItems()}
        headerMessage={getContextMenuHeaderMessage()}
      />
    </Box>
  );
};

export const LoadingExperienceDrawerContent = () => {
  const theme = useTheme();

  return (
    <Grid container data-testid={DATA_TEST_ID.LOADING_EXPERIENCES_DRAWER_CONTENT_CONTAINER}>
      <Grid size={12}>
        <Box
          display="flex"
          flexDirection="column"
          gap={theme.fixedSpacing(theme.tabiyaSpacing.xs)}
          sx={{ paddingBottom: theme.fixedSpacing(theme.tabiyaSpacing.xl) }}
        >
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Skeleton variant="text" width="50%" sx={{ minHeight: 24 }} data-testid="skeleton-text" />
            <Skeleton variant="circular" width={32} height={32} data-testid="skeleton-icon" />
          </Box>
          <Skeleton variant="text" width="100%" sx={{ minHeight: 20 }} data-testid="skeleton-text" />
          <Skeleton variant="text" width="80%" sx={{ minHeight: 20 }} data-testid="skeleton-text" />
          <Box display="flex" alignItems="center" gap={1}>
            <Skeleton variant="text" width={90} sx={{ minHeight: 24 }} data-testid="skeleton-text" />
            <Skeleton variant="circular" width={20} height={20} data-testid="skeleton-icon" />
          </Box>
          <Box display="flex" flexWrap="wrap" gap={2}>
            {[120, 150, 100].map((width) => (
              <Skeleton
                key={width}
                variant="rounded"
                width={width}
                height={32}
                sx={{ borderRadius: 16 }}
                data-testid="skeleton-chip"
              />
            ))}
          </Box>
        </Box>
      </Grid>
    </Grid>
  );
};

export default ExperiencesDrawerContent;
